/**
 * <heritage-carousel>
 *
 * Editorial fade-and-rotate carousel. Slides are direct children with the
 * `data-slide` attribute. Optional companion controls (prev/next buttons
 * and a dot list) live inside the same element with `data-prev`,
 * `data-next`, and `data-dot` attributes.
 *
 * Configuration via attributes:
 *   data-autoplay      "true" | "false"   (default: true)
 *   data-interval-ms   integer ms          (default: 8000)
 *
 * Accessibility:
 *   - Pauses autoplay on hover and on focus-within.
 *   - Honors prefers-reduced-motion (no autoplay; no slide cross-fade
 *     beyond CSS-controlled minimal transition).
 *   - Slides receive aria-hidden when inactive.
 *
 * Communication: dispatches `heritage:carousel:change` with detail
 * `{ index }` whenever the active slide changes.
 */
class HeritageCarousel extends HTMLElement {
  connectedCallback() {
    this._slides = Array.from(this.querySelectorAll('[data-slide]'));
    if (this._slides.length === 0) return;

    this._dots = Array.from(this.querySelectorAll('[data-dot]'));
    this._prev = this.querySelector('[data-prev]');
    this._next = this.querySelector('[data-next]');

    this._reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
    this._autoplay = this.dataset.autoplay !== 'false' && !this._reducedMotion;
    this._intervalMs = parseInt(this.dataset.intervalMs, 10) || 8000;
    this._index = this._slides.findIndex((s) => s.classList.contains('active'));
    if (this._index < 0) this._index = 0;
    this._timer = null;

    this._slides.forEach((s, i) => {
      s.setAttribute('aria-hidden', i === this._index ? 'false' : 'true');
    });

    this._onPrev = () => this.go(this._index - 1);
    this._onNext = () => this.go(this._index + 1);
    this._onDotClick = (e) => {
      const target = e.currentTarget;
      const idx = parseInt(target.dataset.dot, 10);
      if (!Number.isNaN(idx)) this.go(idx);
    };
    this._onMouseEnter = () => this._stopTimer();
    this._onMouseLeave = () => this._startTimer();
    this._onFocusIn = () => this._stopTimer();
    this._onFocusOut = () => this._startTimer();

    if (this._prev) this._prev.addEventListener('click', this._onPrev);
    if (this._next) this._next.addEventListener('click', this._onNext);
    this._dots.forEach((d) => d.addEventListener('click', this._onDotClick));
    this.addEventListener('mouseenter', this._onMouseEnter);
    this.addEventListener('mouseleave', this._onMouseLeave);
    this.addEventListener('focusin', this._onFocusIn);
    this.addEventListener('focusout', this._onFocusOut);

    this._startTimer();
  }

  disconnectedCallback() {
    this._stopTimer();
    if (this._prev) this._prev.removeEventListener('click', this._onPrev);
    if (this._next) this._next.removeEventListener('click', this._onNext);
    this._dots.forEach((d) => d.removeEventListener('click', this._onDotClick));
    this.removeEventListener('mouseenter', this._onMouseEnter);
    this.removeEventListener('mouseleave', this._onMouseLeave);
    this.removeEventListener('focusin', this._onFocusIn);
    this.removeEventListener('focusout', this._onFocusOut);
  }

  go(target) {
    const len = this._slides.length;
    const next = ((target % len) + len) % len;
    if (next === this._index) return;

    const prevSlide = this._slides[this._index];
    const prevDot = this._dots[this._index];
    const nextSlide = this._slides[next];
    const nextDot = this._dots[next];

    prevSlide.classList.remove('active');
    prevSlide.setAttribute('aria-hidden', 'true');
    if (prevDot) prevDot.classList.remove('active');

    nextSlide.classList.add('active');
    nextSlide.setAttribute('aria-hidden', 'false');
    if (nextDot) {
      nextDot.classList.add('active');
      // restart progress animation by reflowing the dot
      void nextDot.offsetWidth;
    }

    this._index = next;
    this._startTimer();
    this.dispatchEvent(new CustomEvent('heritage:carousel:change', {
      bubbles: true,
      detail: { index: next },
    }));
  }

  _startTimer() {
    this._stopTimer();
    if (!this._autoplay || this._slides.length < 2) return;
    this._timer = setInterval(() => this.go(this._index + 1), this._intervalMs);
  }

  _stopTimer() {
    if (this._timer) {
      clearInterval(this._timer);
      this._timer = null;
    }
  }
}

if (!customElements.get('heritage-carousel')) {
  customElements.define('heritage-carousel', HeritageCarousel);
}
